import { computed } from 'vue'
// types
import type { ComputedRef } from 'vue'
import type { BlockStatsItemProps } from '@ts-blocks/block-stats-item/interfaces'
import type { BlockMediaCardProps } from '@ts-blocks/block-media-card/interfaces'
import type { Button, SectionHeroImageProps } from '@ts-sections/section-hero-image/interfaces'
// hooks
import { useDefaultCtaUrl } from '@composables/tools/default-cta-url.hook'
import { useGlobalStats } from '@composables/tools/global-stats.hook'
import { useEventsSection } from '@hooks/events/sections/events-section.hook'
// helpers
import { toAnalyticsId } from '@helpers/analytics'
import { resolveStrapiMediaUrl, buildStrapiSrcset, STRAPI_IMAGE_SIZES } from '@helpers/strapi-media'

interface HeroCtaView {
  key: string
  view: 'primary' | 'secondary'
  text: string
  href: string
}

interface SectionHeroImageHookOptions {
  section: () => SectionHeroImageProps
}

interface SectionHeroImageHookReturn {
  refTitle: ComputedRef<string>
  refSubtitle: ComputedRef<string>
  refImageAlt: ComputedRef<string>
  refMediaCard: ComputedRef<BlockMediaCardProps | null>
  refHasVideo: ComputedRef<boolean>
  refCtas: ComputedRef<HeroCtaView[]>
  refStats: ComputedRef<BlockStatsItemProps[]>
  refShowStats: ComputedRef<boolean>
  handleClickCta: (cta: HeroCtaView) => void
}

export const useSectionHeroImage = ({ section }: SectionHeroImageHookOptions): SectionHeroImageHookReturn => {
  const { public: { strapi } } = useRuntimeConfig()
  const refDefaultCtaUrl = useDefaultCtaUrl()
  const { buildCtaHref, trackSectionEvent } = useEventsSection({ sectionName: 'hero-image' })
  const refGlobalStats = useGlobalStats()

  const refTitle = computed<string>(() => section().title ?? '')

  const refSubtitle = computed<string>(() => section().subtitle ?? '')

  const refImageAlt = computed<string>(() => section().image?.alternativeText ?? refTitle.value)

  const refMediaCard = computed<BlockMediaCardProps | null>(() => section().mediaCard ?? null)

  const refHasVideo = computed<boolean>(() => Boolean(refMediaCard.value?.videoUrl))

  const refPreloadMedia = computed(() => (refHasVideo.value ? refMediaCard.value?.thumbnail : section().image) ?? null)

  useHead({
    link: () => {
      const media = refPreloadMedia.value
      if (!media?.url) return []
      return [{
        rel: 'preload',
        as: 'image',
        href: resolveStrapiMediaUrl(media.url, strapi.publicUrl),
        imagesrcset: buildStrapiSrcset(media, strapi.publicUrl) || undefined,
        imagesizes: STRAPI_IMAGE_SIZES,
        fetchpriority: 'high',
      }]
    },
  })

  const toCta =(key: string, view: HeroCtaView['view'], button: Button | null | undefined): HeroCtaView | null => {
    const text = button?.buttonText ?? ''
    if (!text) return null
    const base = button?.isDefaultUrl !== false ? refDefaultCtaUrl.value : (button?.buttonLink || '#')
    return { key, view, text, href: buildCtaHref(base, { text }) }
  }

  const refCtas = computed<HeroCtaView[]>(() => {
    const s = section()
    return [
      toCta('primary', 'primary', s.primaryCta),
      s.showSecondaryCta !== false ? toCta('secondary', 'secondary', s.secondaryCta) : null,
      s.showTertiaryCta !== false ? toCta('tertiary', 'secondary', s.tertiaryCta) : null,
    ].filter((cta): cta is HeroCtaView => cta !== null)
  })

  const refStats = computed<BlockStatsItemProps[]>(() => {
    const stats = section().stats
    return stats?.useGlobalStats === false ? (stats.items ?? []) : refGlobalStats.value
  })

  const refShowStats = computed<boolean>(() => (
    section().showStatsBar !== false && refStats.value.length > 0
  ))

  const handleClickCta = (cta: HeroCtaView): void => {
    trackSectionEvent('Button_click', { name: toAnalyticsId(cta.key), text: cta.text, url: cta.href })
  }

  return {
    refTitle,
    refSubtitle,
    refImageAlt,
    refMediaCard,
    refHasVideo,
    refCtas,
    refStats,
    refShowStats,
    handleClickCta,
  }
}
